import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';

import { Button, Input, Icon } from 'modules/common';
import { formatAmount } from 'core/utilities';
import '../../widgets.scss';

function WidgetPreviewAmountNudge({ widget, donationAmounts, page, editedUpsell }) {
  const [currentNudgeTarget, setCurrentNudgeTarget] = useState(null);
  const [isShowingAcceptMessage, setIsShowingAcceptMessage] = useState(false);
  const [nudgeVisible, setNudgeVisible] = useState(false);
  const [nudgePosition, setNudgePosition] = useState({ top: 0, left: 0, tailLeft: 50 });
  const [nudgeMessage, setNudgeMessage] = useState('');
  const [nudgeColor, setNudgeColor] = useState(widget.amountNudgeContainerColor || '#FFCD84');
  const [nudgeTextColor, setNudgeTextColor] = useState(widget.amountNudgeContainerTextColor || '#191E2A');

  const resizeTimeoutRef = useRef(null);
  const currentNudgeTargetRef = useRef(null);
  const nudgeVisibleRef = useRef(false);
  const hideTimeoutRef = useRef(null);
  const minDisplayTimeRef = useRef(null);

  // Filter out destroyed amounts and ensure we're using the page amounts customization
  // Preserve the order from pageAmounts (by position if available, otherwise by array order)
  const amounts = (donationAmounts || [])
    .map((amt, originalIndex) => ({
      ...amt,
      amount: typeof amt.amount === 'string' ? parseFloat(amt.amount.replace(/[^0-9.]/g, '')) || 0 : amt.amount || 0,
      originalIndex, // Preserve original index for sorting
    }))
    .filter(amt => !amt._destroy && !amt.destroy && amt.amount > 0) // Filter out destroyed and invalid amounts
    .sort((a, b) => {
      // Sort by position if available, otherwise by original index to preserve order
      if (a.position != null && b.position != null) {
        return a.position - b.position;
      }
      if (a.position != null) return -1;
      if (b.position != null) return 1;
      return a.originalIndex - b.originalIndex;
    });
  const maximumAmount = widget.amountNudgeMaximumAmount || 0;
  const showAcceptMessage =
    widget.amountNudgeShowAcceptMessage === true ||
    widget.amountNudgeShowAcceptMessage === '1' ||
    widget.amountNudgeShowAcceptMessage === 'true';
  const acceptMessage = widget.amountNudgeAcceptMessage || 'Thank you for stepping up! Your support truly makes a difference.';
  const originalMessage = widget.amountNudgeMessage || 'Supporters like you power this movement. Can you increase your gift to the next level?';

  // Update refs when state changes
  useEffect(() => {
    currentNudgeTargetRef.current = currentNudgeTarget;
  }, [currentNudgeTarget]);

  useEffect(() => {
    nudgeVisibleRef.current = nudgeVisible;
  }, [nudgeVisible]);

  const getNextAmountButton = (currentIndex, currentAmount) => {
    // Get the next higher amount button, regardless of maximum
    // The maximum check is done in handleButtonClick based on the selected amount
    const nextButtons = amounts
      .map((amt, index) => ({ ...amt, index }))
      .filter(amt => {
        const buttonAmount = amt.amount;
        return buttonAmount > currentAmount;
      })
      .sort((a, b) => a.amount - b.amount);

    return nextButtons.length > 0 ? nextButtons[0] : null;
  };

  const positionNudgeToButton = useCallback(
    targetIndex => {
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        const containerElement = document.querySelector('.widget-preview_amountNudge');
        const buttonWrapperElement = document.querySelector('.widget-preview_amountNudge_btn-wrapper');
        const targetButtonElement = document.querySelector(`[data-amount-index="${targetIndex}"]`);

        if (containerElement && buttonWrapperElement && targetButtonElement) {
          const containerRect = containerElement.getBoundingClientRect();
          const targetRect = targetButtonElement.getBoundingClientRect();

          const bubbleWidth = 300;
          const minVerticalGap = 10;

          // Measure the ACTUAL bubble height by creating a temporary element
          const tempBubble = document.createElement('div');
          tempBubble.style.position = 'absolute';
          tempBubble.style.visibility = 'hidden';
          tempBubble.style.width = `${bubbleWidth}px`;
          tempBubble.style.minHeight = '50px';
          tempBubble.style.padding = '10px 30px 10px 10px';
          tempBubble.style.fontSize = '14px';
          tempBubble.style.lineHeight = '1.4';
          tempBubble.textContent = nudgeMessage;
          document.body.appendChild(tempBubble);

          const actualBubbleHeight = tempBubble.offsetHeight;
          document.body.removeChild(tempBubble);

          // Now position using the ACTUAL height
          const relativeTop = targetRect.top - containerRect.top - actualBubbleHeight - minVerticalGap;
          const relativeLeft = targetRect.left - containerRect.left + targetRect.width / 2 - bubbleWidth / 2;

          // Calculate tail to always point to center of target button
          const targetCenterX = targetRect.left - containerRect.left + targetRect.width / 2;
          const bubbleCenterX = relativeLeft + bubbleWidth / 2;
          const centerOffset = targetCenterX - bubbleCenterX;
          const tailLeft = 50 + (centerOffset / bubbleWidth) * 100;
          const clampedTailLeft = Math.max(10, Math.min(90, tailLeft));

          setNudgePosition({
            top: relativeTop,
            left: relativeLeft,
            tailLeft: clampedTailLeft,
            isFirstRow: targetIndex < 4,
          });
        }
      });
    },
    [nudgeMessage] // Add nudgeMessage back as dependency
  );

  // Handle window resize with debouncing
  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        // Use refs to get current values during resize
        if (nudgeVisibleRef.current && currentNudgeTargetRef.current) {
          positionNudgeToButton(currentNudgeTargetRef.current.index);
        }
      }, 150); // Slightly longer debounce
    };

    // Always add the resize listener, check conditions inside
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [positionNudgeToButton]); // Only depend on positionNudgeToButton

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (minDisplayTimeRef.current) {
        clearTimeout(minDisplayTimeRef.current);
      }
    };
  }, []);

  // Reposition when nudge target or visibility changes
  useEffect(() => {
    if (nudgeVisible && currentNudgeTarget) {
      positionNudgeToButton(currentNudgeTarget.index);
    }
  }, [currentNudgeTarget, nudgeVisible, positionNudgeToButton]);

  const handleCloseNudge = () => {
    // If within minimum display time, delay the close
    if (minDisplayTimeRef.current) {
      // Clear the existing timeout to prevent memory leak
      clearTimeout(minDisplayTimeRef.current);
      minDisplayTimeRef.current = setTimeout(() => {
        setNudgeVisible(false);
        setCurrentNudgeTarget(null);
        setIsShowingAcceptMessage(false);
        minDisplayTimeRef.current = null;
      }, 100); // Small delay to ensure smooth UX
      return;
    }

    // Clear any pending timeouts
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    setNudgeVisible(false);
    setCurrentNudgeTarget(null);
    setIsShowingAcceptMessage(false);
  };

  const handleButtonClick = (clickedIndex, clickedAmount) => {
    // Clear any existing timeouts
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (minDisplayTimeRef.current) {
      clearTimeout(minDisplayTimeRef.current);
      minDisplayTimeRef.current = null;
    }

    // If clicking the same button that triggered the current nudge
    if (currentNudgeTarget && clickedIndex === currentNudgeTarget.index) {
      if (showAcceptMessage) {
        setNudgeMessage(acceptMessage);
        setNudgeColor(widget.amountNudgeAcceptContainerColor || '#8DD1A4');
        setNudgeTextColor(widget.amountNudgeAcceptContainerTextColor || '#191E2A');
        setIsShowingAcceptMessage(true);
        setNudgeVisible(true);

        // Set a minimum display time for accept message, then auto-hide
        hideTimeoutRef.current = setTimeout(() => {
          setNudgeVisible(false);
          setCurrentNudgeTarget(null);
          setIsShowingAcceptMessage(false);
          hideTimeoutRef.current = null;
        }, 4000); // Increased from 3000 to 4000ms for better visibility
      } else {
        setNudgeVisible(false);
        setCurrentNudgeTarget(null);
        setIsShowingAcceptMessage(false);
      }
      return;
    }

    // Reset accept message state if we were showing it
    if (isShowingAcceptMessage) {
      setNudgeColor(widget.amountNudgeContainerColor || '#FFCD84');
      setNudgeTextColor(widget.amountNudgeContainerTextColor || '#191E2A');
      setIsShowingAcceptMessage(false);
    }
    setNudgeMessage(originalMessage);

    const nextButton = getNextAmountButton(clickedIndex, clickedAmount);

    if (nextButton) {
      // Show nudge if selected amount is < maximum (exclusive), even if next amount exceeds maximum
      if (maximumAmount > 0 && clickedAmount >= maximumAmount) {
        setNudgeVisible(false);
        setCurrentNudgeTarget(null);
        return;
      }

      // Set up the new nudge
      setCurrentNudgeTarget(nextButton);
      setNudgeMessage(originalMessage);
      setNudgeColor(widget.amountNudgeContainerColor || '#FFCD84');
      setNudgeTextColor(widget.amountNudgeContainerTextColor || '#191E2A');
      setNudgeVisible(true);

      // Ensure minimum display time of 2 seconds for new nudges
      minDisplayTimeRef.current = setTimeout(() => {
        minDisplayTimeRef.current = null;
      }, 2000);
    } else {
      setNudgeVisible(false);
      setCurrentNudgeTarget(null);
    }
  };

  if (!amounts || amounts.length === 0) {
    return (
      <div className="text-center text-muted p-4">
        <p>No donation amounts configured for this page.</p>
      </div>
    );
  }

  return (
    <>
      <Container className="widget-preview_amountNudge" style={{ position: 'relative' }}>
        <Row className="d-flex justify-content-center">
          <Col xs={12} className="widget-preview_amountNudge_btn-wrapper">
            {amounts.map((amt, index) => {
              return (
                <Button
                  key={amt.id}
                  data-amount-index={index}
                  data-amount={amt.amount}
                  style={{
                    backgroundColor: widget.amountButtonAnimationColor,
                  }}
                  onClick={() => {
                    handleButtonClick(index, amt.amount);
                  }}
                >
                  {formatAmount(amt.amount, false, false)}
                </Button>
              );
            })}
            {(page?.hasOtherField || editedUpsell?.include_other_field) && (
              <Input
                moneyInput
                classNameWrapper="widget-preview_amountNudge_btn-other"
                id="otherDonation"
                name="Other"
                value=""
                placeholder="Other"
                disabled
                onChange={() => {}}
              />
            )}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs={12} className="text-center">
            <p className="text-muted" style={{ fontSize: '12px', marginBottom: 0 }}>
              The widget will trigger multiple times in this preview so you can test its functionality. On the live page, it will trigger only once per session.
            </p>
          </Col>
        </Row>

        {nudgeVisible && (
          <div
            className="amount-nudge-bubble"
            style={{
              top: `${nudgePosition.top}px`,
              left: `${nudgePosition.left}px`,
            }}
          >
            <div
              className="amount-nudge-message"
              style={{
                backgroundColor: nudgeColor,
                color: nudgeTextColor,
              }}
            >
              <div>{nudgeMessage}</div>
              <Icon
                icon="times"
                className="position-absolute pointer amount-nudge-close"
                style={{ color: nudgeTextColor }}
                onClick={handleCloseNudge}
              />
            </div>
            <div
              className="amount-nudge-tail"
              style={{
                left: `${nudgePosition.tailLeft}%`,
                borderTop: `8px solid ${nudgeColor}`,
              }}
            />
          </div>
        )}
      </Container>
    </>
  );
}

WidgetPreviewAmountNudge.propTypes = {
  widget: PropTypes.object.isRequired,
  donationAmounts: PropTypes.array.isRequired,
  page: PropTypes.object,
  editedUpsell: PropTypes.object,
};

export default WidgetPreviewAmountNudge;
